import { fetchBaseQuery } from '@reduxjs/toolkit/query'
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query'
import { Mutex } from 'async-mutex'

import { setOnlineStatus } from '@store/actionSlices/statusIndicator'
import { RootStateTypeExtra } from '@store/types'

import prepareHeaders from '@api/headers'

import {
  getCacheVersion,
  getStockCacheVersion,
} from '@utilities/cache-version-utils'
import {
  fetchResponseFromLocalStorage,
  isExistsInTheOfflineEndpointList,
  isOffline,
  storeResponseToLocalStorage,
} from '@utilities/offline-handler-util'
import renewUserAccessToken from '@utilities/renew-access-token-helper'

import { isTokenExpired } from '@adUtilities/token-helper'

const mutex = new Mutex()

const baseUrl = process.env.REACT_APP_API_URL

const baseQuery = fetchBaseQuery({ baseUrl, prepareHeaders })

const BUILDING_URL_PATTERN = /\/v\d+\/[^/]+\/building\b/

const isBuildingUrl = (url: string): boolean => BUILDING_URL_PATTERN.test(url)

const getOfflineResponse = (args: FetchArgs) => {
  if (!isOffline()) {
    return null
  }

  if (!isExistsInTheOfflineEndpointList(args)) {
    return null
  }

  const response = fetchResponseFromLocalStorage(args)

  if (!response) {
    return null
  }

  return response
}

const appendCacheVersion = (args: string | FetchArgs): string | FetchArgs => {
  const isGet =
    typeof args === 'string' ||
    !args.method ||
    args.method.toUpperCase() === 'GET'

  if (!isGet) {
    return args
  }

  const url = typeof args === 'string' ? args : args.url
  const isBuildingEndpoint = isBuildingUrl(url)
  const paramName = isBuildingEndpoint ? 'stockCache' : 'version'
  const paramValue = isBuildingEndpoint
    ? getStockCacheVersion()
    : getCacheVersion()

  if (!paramValue) {
    return args
  }

  if (typeof args === 'string') {
    return `${args}${args.includes('?') ? '&' : '?'}${paramName}=${encodeURIComponent(paramValue)}`
  }

  return {
    ...args,
    params: { ...args.params, [paramName]: paramValue },
  }
}

const baseQueryInterceptor: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let fetchedFromAPI = false
  let isOnline = true

  const { token } = api.getState() as RootStateTypeExtra

  await mutex.waitForUnlock()

  if (isTokenExpired(token) && !mutex.isLocked()) {
    const release = await mutex.acquire()
    try {
      await renewUserAccessToken(token, api, extraOptions)
    } finally {
      release()
    }
  } else {
    await mutex.waitForUnlock()
  }

  const fetchArgsObject = typeof args === 'string' ? { url: args } : args

  let response: any = getOfflineResponse(fetchArgsObject)

  if (!response) {
    const argsWithCacheVersion = appendCacheVersion(args)
    response = await baseQuery(argsWithCacheVersion, api, extraOptions)

    if (response) {
      fetchedFromAPI = true

      if (response.error) {
        const { status } = response.error

        if (status === 401) {
          if (!mutex.isLocked()) {
            const release = await mutex.acquire()

            try {
              const renewStatus = await renewUserAccessToken(
                token,
                api,
                extraOptions
              )
              if (renewStatus) {
                response = await baseQuery(args, api, extraOptions)
              }
            } finally {
              release()
            }
          } else {
            await mutex.waitForUnlock()
            response = await baseQuery(args, api, extraOptions)
          }
        }

        if (status === 'FETCH_ERROR') {
          isOnline = false
        }
      }
    } else {
      isOnline = false
    }
  }

  if (
    isOffline() &&
    fetchedFromAPI &&
    !response.error &&
    isExistsInTheOfflineEndpointList(fetchArgsObject)
  ) {
    storeResponseToLocalStorage(fetchArgsObject, response)
  }

  api.dispatch(setOnlineStatus(isOnline))

  return response
}

export default baseQueryInterceptor
